import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { imageSrc } from "../lib/api";
import { curated } from "../lib/curate";
import { EN, feelingName, RAINBOW_ORDER, SECTIONS } from "../lib/sections";
import { FavouriteMark, StoryMark } from "./FavouriteMark";
import { StoryCard } from "./gallery/StoryCard";

const btn = "grid h-12 w-12 place-items-center rounded-full border border-white/70 bg-[#FBF7F2]/90 text-[#4A2D55] shadow-[0_10px_30px_-18px_rgba(60,30,80,0.6)] transition-transform duration-200 hover:-translate-y-0.5";

// When 3D can't run, each feeling room still opens on its painting 1, one painting at a time, with ‹ › and a finale.
export default function Rooms2D({ paintings, onOpen, onCatalogue, webgl, onSwitch3D, startRoom = null }) {
  const rooms = useMemo(() => RAINBOW_ORDER.map((id) => [id, curated(paintings).filter((p) => p.section === id)]).filter(([, l]) => l.length), [paintings]);
  const [r, setR] = useState(() => Math.max(0, rooms.findIndex(([id]) => id === startRoom)));
  const [i, setI] = useState(0);
  const [end, setEnd] = useState(false);
  const swipe = useRef(null);
  const [id, list] = rooms[r] || [null, []];
  const p = list[i];
  const go = (dir) => {
    if (end) return;
    if (dir > 0 && i === list.length - 1) return setEnd(true);
    setI((i + dir + list.length) % list.length);
  };
  const enter = (k) => {
    setR(k);
    setI(0);
    setEnd(false);
  };
  useEffect(() => {
    const key = (e) => (e.key === "ArrowRight" ? go(1) : e.key === "ArrowLeft" ? go(-1) : null);
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  if (!p) return null;
  const ar = p.aspect_ratio || (p.width && p.height ? p.width / p.height : 1);
  return (
    <div
      data-testid="rooms-2d"
      data-room={id}
      className="absolute inset-0 flex flex-col overflow-hidden bg-[#F3ECF6]"
      onPointerDown={(e) => (swipe.current = e.clientX)}
      onPointerUp={(e) => {
        const dx = e.clientX - (swipe.current ?? e.clientX);
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
      }}
    >
      <nav className="flex flex-wrap justify-center gap-2 px-14 pt-24 sm:pt-20">
        {rooms.map(([rid], k) => (
          <button key={rid} data-testid={`rooms-2d-room-${rid}`} data-active={k === r ? "true" : "false"} onClick={() => enter(k)} className={`rounded-full px-4 py-1 font-display text-lg italic transition-colors duration-200 ${k === r ? "bg-white text-[#2D1E3E]" : "text-[#6B5A7A] hover:bg-white/60"}`} style={k === r ? { boxShadow: `0 0 0 1px ${SECTIONS[rid].tint}` } : undefined}>
            {SECTIONS[rid].name} <span className="not-italic">· {EN[rid]}</span>
          </button>
        ))}
      </nav>
      <p className="mt-2 text-center font-display text-base italic text-[#6B5A7A]">{SECTIONS[id].line}</p>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-16 py-6">
        <button data-testid="room-prev-button" aria-label="previous painting" onClick={() => go(-1)} className={`${btn} absolute left-3`}>
          <ChevronLeft size={22} />
        </button>
        <button data-testid="rooms-2d-painting" onClick={() => onOpen(p)} aria-label={`Step inside ${p.title}`} className="block h-full max-h-[62vh] bg-[#2E2622] p-[3px] shadow-[0_28px_50px_-26px_rgba(40,28,24,0.55)]" style={{ aspectRatio: `${ar}`, maxWidth: "100%" }}>
          <span className="block h-full w-full bg-[#FBF8F3] p-3">
            <img key={p.id} src={imageSrc(p.image_url)} alt={p.title} className="h-full w-full object-contain" draggable={false} />
          </span>
        </button>
        <button data-testid="room-next-button" aria-label="next painting" onClick={() => go(1)} className={`${btn} absolute right-3`}>
          <ChevronRight size={22} />
        </button>
        {end && (
          <div data-testid="room-end-panel" className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#F3ECF6]/85 backdrop-blur-sm">
            <p className="font-display text-3xl italic text-[#2D1E3E]">{feelingName(id)}</p>
            {r + 1 < rooms.length && (
              <button data-testid="room-end-next" onClick={() => enter(r + 1)} className="font-display text-xl text-[#7E57C2]">
                next feeling ›
              </button>
            )}
            <button data-testid="room-end-back" onClick={() => enter(r)} className="font-display text-xl text-[#4A2D55]">
              see this room again
            </button>
            {r + 1 >= rooms.length && (
              <button data-testid="room-end-memories" onClick={() => window.__afaeSit?.()} className="font-display text-base italic text-[#5C4A72]">
                or, to end, sit with the memories ›
              </button>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col items-center gap-1 pb-10">
        <p data-testid="room-counter" className="rounded-full bg-white/60 px-4 py-1 font-display text-base text-[#4A2D55]">
          {i + 1} of {list.length}
        </p>
        <p className="font-display text-lg italic text-[#2D1E3E]">{p.title}</p>
        <FavouriteMark painting={p} testid="rooms-2d-favourite" />
        <StoryMark painting={p} testid="rooms-2d-story" />
        <StoryCard painting={p} className="mt-2 max-w-[92vw]" />
        <div className="flex gap-4 text-sm">
          <button data-testid="rooms-2d-catalogue" onClick={onCatalogue} className="text-[#7E57C2] underline-offset-4 hover:underline">
            all paintings
          </button>
          {webgl && (
            <button data-testid="switch-to-3d-button" onClick={onSwitch3D} className="text-[#7E57C2] underline-offset-4 hover:underline">
              try the rooms in 3D
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
